import { useState } from "react"
import { Link } from "@inertiajs/react"
import { ThumbsUp, ThumbsDown, Info } from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { TicketHistoryRow, TicketPriority, TicketStatus } from "@/types/ticket"

type Props = { rows: TicketHistoryRow[] }

// Vertical timeline of past tickets for this requester. Each row visually
// encodes the convo's full lifecycle, not just the current state — see
// `markersFor()` for the dot color (final outcome) + halo (path the
// convo took to get there). Click-through Link goes to that ticket.
export function TicketHistoryPanel({ rows }: Props) {
  const [legendOpen, setLegendOpen] = useState(false)
  return (
    <aside className="flex w-full flex-col gap-3 overflow-y-auto rounded-card border border-border bg-app-panel2/40 p-4">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-app-text">Interaction history</h2>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-app-muted">{rows.length === 50 ? "50+" : rows.length}</span>
          <button
            type="button"
            onClick={() => setLegendOpen((v) => !v)}
            className="rounded-control p-1 text-app-muted hover:bg-white/5 hover:text-app-text"
            title="Show legend"
            aria-label="Show legend"
          >
            <Info size={13} strokeWidth={2} />
          </button>
        </div>
      </header>
      {legendOpen && <Legend onClose={() => setLegendOpen(false)} />}
      {rows.length === 0 ? (
        <p className="text-xs text-app-muted">No prior tickets from this user.</p>
      ) : (
        <ol className="relative ml-3 space-y-4 border-l border-border pl-5">
          {rows.map((r) => <HistoryRow key={r.id} row={r} />)}
        </ol>
      )}
    </aside>
  )
}

type DotTone = "green" | "red" | "gold" | "muted"
type HaloTone = "yellow" | "red" | null

type Markers = {
  dot:        DotTone
  halo:       HaloTone
  outcomeText: string
}

// Resolution-bucket rawStates from Portal::TicketPresenter::STATE_TO_STATUS.
// Anything in this set displays as `status === "closed"` on the FE.
const RESOLVED_RAW = new Set(["resolved", "auto_resolved", "assumed_resolved"])
const LOCKOUT_RAW  = new Set(["rage_locked", "attempted_hack"])

// Decide the dot + halo tones from the row's lifecycle. Order of precedence:
//   1. Still open (status=open ← rawState=escalated) → red dot.
//   2. Pending (rawState=open) / pending_feedback   → gold dot.
//   3. Closed bucket:
//      a. rawState in LOCKOUT_RAW (rage_locked / attempted_hack) → green dot,
//         red halo. Convo ended in a lockout but the ticket is technically
//         closed; treat the abuse exit path as the dominant signal.
//      b. hadLockoutEvent (warning fired earlier, convo still resolved) →
//         green dot, red halo (same — the lockout signal is what matters).
//      c. escalated → green dot, yellow halo.
//      d. else → green dot, no halo (clean AI handle).
function markersFor(row: TicketHistoryRow): Markers {
  if (row.status === "open") {
    return { dot: "red", halo: null, outcomeText: "Open / escalated, awaiting human" }
  }
  if (row.status === "pending" || row.status === "pending_feedback") {
    return { dot: "gold", halo: null, outcomeText: row.status === "pending_feedback" ? "Awaiting feedback" : "Pending" }
  }
  if (LOCKOUT_RAW.has(row.rawState)) {
    return { dot: "green", halo: "red", outcomeText: row.rawState === "attempted_hack" ? "Closed after attempted hack" : "Closed after rage lockout" }
  }
  if (RESOLVED_RAW.has(row.rawState)) {
    if (row.hadLockoutEvent) return { dot: "green", halo: "red",    outcomeText: "Resolved after lockout event" }
    if (row.escalated)       return { dot: "green", halo: "yellow", outcomeText: "Escalated, then resolved" }
    return { dot: "green", halo: null, outcomeText: "Resolved by AI" }
  }
  return { dot: "muted", halo: null, outcomeText: statusLabel(row.status) }
}

function HistoryRow({ row }: { row: TicketHistoryRow }) {
  const { dot, halo, outcomeText } = markersFor(row)
  const showPriorityChip = row.priority === "urgent" || row.priority === "high"
  return (
    <li className="relative">
      <Marker dot={dot} halo={halo} placement="rail" />
      <Link
        href={`/tickets/${row.id}`}
        className="block rounded-control border border-transparent px-2 py-1 transition hover:border-border hover:bg-white/5"
      >
        <div className="flex items-center gap-1.5">
          <div className="text-sm font-medium text-app-text">Ticket #{row.id}</div>
          {showPriorityChip && <PriorityChip priority={row.priority} />}
          {row.userRating === "up" && (
            <ThumbsUp size={11} strokeWidth={2} className="text-status-success" aria-label="Rated 👍" />
          )}
          {row.userRating === "down" && (
            <ThumbsDown size={11} strokeWidth={2} className="text-status-error" aria-label="Rated 👎" />
          )}
        </div>
        <div className="text-[11px] text-app-muted">{fmtCompactRelative(row.createdAt)}</div>
        <div className="text-[11px] text-app-muted">{outcomeText}</div>
      </Link>
    </li>
  )
}

// The marker is a 12px square (matching the original timeline) with an
// optional outer halo — a 16px ring around the dot, in either yellow
// (escalated) or red (lockout event). Halos use the SAME center color so
// the dot itself reads as the outcome. `placement="rail"` anchors it on
// the timeline's left rail; "inline" leaves it in normal flow for legend
// usage.
function Marker({ dot, halo, placement = "inline" }: {
  dot: DotTone
  halo: HaloTone
  placement?: "rail" | "inline"
}) {
  const dotClass =
    dot === "green"  ? "border-status-success bg-status-success" :
    dot === "red"    ? "border-brand-red bg-brand-red" :
    dot === "gold"   ? "border-status-warning bg-status-warning" :
                       "border-border bg-app-panel2"
  const haloClass =
    halo === "yellow" ? "ring-2 ring-status-warning ring-offset-2 ring-offset-app-panel2" :
    halo === "red"    ? "ring-2 ring-status-error ring-offset-2 ring-offset-app-panel2" :
                        ""
  const placementClass =
    placement === "rail" ? "absolute -left-[26px] top-1" : "inline-block"
  return (
    <span
      className={cn(
        placementClass,
        "h-3 w-3 rounded-sm border",
        dotClass,
        haloClass,
      )}
      aria-hidden
    />
  )
}

function PriorityChip({ priority }: { priority: TicketPriority }) {
  const tone =
    priority === "urgent" ? "border-status-error/40 bg-status-error/10 text-status-error" :
    priority === "high"   ? "border-status-warning/40 bg-status-warning/10 text-status-warning" :
                            "border-border bg-white/5 text-app-muted"
  const symbol = priority === "urgent" ? "◆" : priority === "high" ? "▲" : "•"
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 rounded-chip border px-1.5 py-px text-[10px] uppercase tracking-wide",
      tone,
    )}>
      <span aria-hidden>{symbol}</span> {priority}
    </span>
  )
}

function Legend({ onClose }: { onClose: () => void }) {
  return (
    <div className="rounded-control border border-border bg-app-bg p-3 text-[11px]">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium text-app-text">Legend</span>
        <button
          type="button"
          onClick={onClose}
          className="text-app-subtle hover:text-app-text"
          aria-label="Close legend"
        >
          ×
        </button>
      </div>
      <ul className="space-y-1.5">
        <LegendRow dot="green"  halo={null}     label="Resolved by AI (no escalation, no abuse)" />
        <LegendRow dot="green"  halo="yellow"   label="Escalated to a human, then resolved" />
        <LegendRow dot="green"  halo="red"      label="Lockout event (rage / abuse) during convo, still resolved" />
        <LegendRow dot="red"    halo={null}     label="Still open" />
        <LegendRow dot="gold"   halo={null}     label="Awaiting feedback / pending" />
        <LegendRow dot="muted"  halo={null}     label="Closed without a clean resolve" />
      </ul>
      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border/60 pt-2 text-app-muted">
        <span className="flex items-center gap-1">
          <ThumbsUp size={11} strokeWidth={2} className="text-status-success" /> 👍 rating
        </span>
        <span className="flex items-center gap-1">
          <ThumbsDown size={11} strokeWidth={2} className="text-status-error" /> 👎 rating
        </span>
        <span>◆ urgent</span>
        <span>▲ high</span>
      </div>
    </div>
  )
}

function LegendRow({ dot, halo, label }: { dot: DotTone; halo: HaloTone; label: string }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        <Marker dot={dot} halo={halo} />
      </span>
      <span className="text-app-muted">{label}</span>
    </li>
  )
}

function statusLabel(s: TicketStatus): string {
  switch (s) {
    case "open":             return "Open"
    case "pending":          return "Pending"
    case "pending_feedback": return "Pending feedback"
    case "closed":           return "Closed"
  }
}
